import React, { useState, useEffect, useContext } from 'react';
import RequestService from '../../services/RequestService';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';

import {
    Grid2 as Grid,
    Typography,
    Breadcrumbs,
    Link,
    TextField,
    Button,
    Box,
    Snackbar,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Alert,
    useMediaQuery,
    Stack,
    CircularProgress,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import AdminNewPlanDialog from '../../widgets/admin/AdminNewPlanDialog';
import TableComponent from '../../widgets/TableComponent';
import { ReactComponent as EditIcon } from '../../assets/icons/edit.svg';
import { toCents } from '../../utils/toCents';
import AdminEditPlanDialog from '../../widgets/admin/AdminEditPlanDialog';
import AdminDeletePlanDialog from '../../widgets/admin/AdminDeletePlanDialog';
// import GameCard from '../../widgets/plans/GameCard';
// import GameList from '../../widgets/plans/GameList';

const AdminPlansPage = () => {
    const [companies, setCompanies] = useState([]);
    const [selectedCompanyId, setSelectedCompanyId] = useState('');
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [selectedPlan, setSelectedPlan] = useState(null);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);

    const [snackbarOpen, setSnackbarOpen] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    useEffect(() => {
        const fetchPlans = async () => {
            setLoading(true);
            try {
                const data = await RequestService.getPlansAsSuperAdmin();
                setPlans(data);
            } catch (err) {
                console.error('Erro ao carregar planos:', err);
                setError('Erro ao carregar planos.');
                setPlans([]);
            } finally {
                setLoading(false);
            }
        };

        fetchPlans();
    }, []);

    const handleRowClick = (game) => {
        setSelectedPlan(game);
        setIsDialogOpen(true);
    };

    const handleCloseDialog = () => {
        setIsDialogOpen(false);
        setSelectedPlan(null);
    };

    const handleOpenAddDialog = () => setIsAddDialogOpen(true);
    const handleCloseAddDialog = () => setIsAddDialogOpen(false);

    const handleEdit = (game) => {
        handleRowClick(game);
    };

    const handleOpenDeleteDialog = (game) => {
        setSelectedPlan(game);
        setIsDeleteDialogOpen(true);
    };

    const handleSuccessAdd = async (message) => {
        try {
            const data = await RequestService.getPlansAsSuperAdmin({
                page: 0,
                size: 10,
            });
            setPlans(data);
            setSnackbarMessage(message || 'Plano criado com sucesso!');
            setSnackbarOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSuccessEdit = async (message) => {
        try {
            const data = await RequestService.getPlansAsSuperAdmin({
                page: 0,
                size: 10,
            });
            setPlans(data);
            setSnackbarMessage(message || 'Plano atualizado com sucesso!');
            setSnackbarOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const handleSuccessDelete = async (message) => {
        try {
            const data = await RequestService.getPlansAsSuperAdmin({
                page: 0,
                size: 10,
            });
            setPlans(data);
            setSnackbarMessage(message || 'Plano excluído com sucesso!');
            setSnackbarOpen(true);
        } catch (err) {
            console.error(err);
        }
    };

    const handleCloseDeleteDialog = () => {
        setIsDeleteDialogOpen(false);
        setSelectedPlan(null);
    };

    const handleSnackbarClose = () => {
        setSnackbarOpen(false);
    };

    const columns = [
        {
            label: 'Nome',
            field: 'name',
            align: 'left',
            renderCell: (value, row) => (
                <Stack direction="column" spacing={1}>
                    <Typography variant="body2" sx={{ color: '#292929' }}>
                        {row.name}
                    </Typography>
                </Stack>
            ),
        },
        {
            label: 'Máximo de usuários',
            field: 'maxUsers',
            align: 'center',
            renderCell: (value, row) => (
                <Typography variant="body2" sx={{ color: '#292929' }}>
                    {row.maxUsers}
                </Typography>
            ),
        },
        {
            label: 'Valor Mensal',
            field: 'priceCents',
            align: 'center',
            renderCell: (value, row) => (
                <Typography variant="body2" sx={{ color: '#292929' }}>
                    {toCents(row.priceCents)}
                </Typography>
            ),
        },
        {
            label: '',
            field: 'actions',
            align: 'right',
            renderCell: (value, row) => (
                <Stack direction="row" spacing={2}>
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => handleRowClick(row)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'blue',
                        }}
                    >
                        <EditIcon sx={{ color: '#1F83FF' }} />
                        <Typography
                            variant="body2"
                            sx={{ ml: 0.5, color: '#1F83FF' }}
                        >
                            Editar
                        </Typography>
                    </Link>
                    <Link
                        component="button"
                        underline="none"
                        onClick={() => handleOpenDeleteDialog(row)}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            color: 'red',
                        }}
                    >
                        <DeleteOutlinedIcon fontSize="small" />
                        <Typography variant="body2" sx={{ ml: 0.5 }}>
                            Excluir
                        </Typography>
                    </Link>
                </Stack>
            ),
        },
    ];

    const renderEmptyState = () => (
        <Box
            sx={{
                textAlign: 'center',
                mt: 4,
                color: '#666',
                fontFamily: 'Poppins',
                fontSize: 18,
            }}
        >
            <Typography variant="h6">Nenhum plano cadastrado.</Typography>
            <Typography variant="body2">
                Clique em <strong>"Novo plano"</strong> para adicionar o
                primeiro!
            </Typography>
        </Box>
    );

    const renderMobileLayout = () => {
        return plans.length === 0 ? (
            renderEmptyState()
        ) : (
            <Typography>lorem</Typography>
            // <GameList
            //     plans={plans}
            //     onUserClick={handleRowClick}
            //     onDeleteClick={handleOpenDeleteDialog}
            // />
        );
    };

    const renderDesktopLayout = () => {
        return plans.length === 0 ? (
            renderEmptyState()
        ) : (
            <Grid container spacing={3} sx={{ mt: 2 }}>
                {plans.map((game, index) => (
                    <Box sx={{ width: '100%', overflowX: 'auto', mt: 2 }}>
                        <TableComponent columns={columns} rows={plans} />
                    </Box>
                ))}
            </Grid>
        );
    };

    return (
        <Box>
            <Grid container justifyContent="start">
                <Breadcrumbs aria-label="breadcrumb">
                    <Link underline="hover" color="inherit" href="/">
                        Início
                    </Link>
                    <Link underline="hover" color="inherit">
                        Definições
                    </Link>
                    <Typography sx={{ color: 'text.primary' }}>
                        Planos
                    </Typography>
                </Breadcrumbs>
            </Grid>

            <Grid container xs={12} sx={{ mt: 3 }}>
                <Typography variant="h4" gutterBottom>
                    Planos
                </Typography>
            </Grid>

            <Grid container sx={12} justifyContent={'end'}>
                <Grid item sx={6}>
                    <Button
                        variant="contained"
                        sx={{ minHeight: '47px' }}
                        endIcon={<AddCircleOutlineOutlinedIcon />}
                        onClick={handleOpenAddDialog}
                    >
                        Novo Plano
                    </Button>
                </Grid>
            </Grid>

            {loading ? (
                <Stack alignItems="center" mt={4}>
                    <CircularProgress />
                </Stack>
            ) : isMobile ? (
                renderMobileLayout()
            ) : (
                renderDesktopLayout()
            )}

            <AdminNewPlanDialog
                open={isAddDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseAddDialog();
                    } else {
                        handleCloseAddDialog();
                    }
                }}
                onSuccess={handleSuccessAdd}
            />

            <AdminEditPlanDialog
                open={isDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDialog();
                    } else {
                        handleCloseDialog();
                    }
                }}
                plan={selectedPlan}
                onSuccess={handleSuccessEdit}
            />

            <AdminDeletePlanDialog
                open={isDeleteDialogOpen}
                onClose={(event, reason) => {
                    if (reason !== 'backdropClick') {
                        handleCloseDeleteDialog();
                    } else {
                        handleCloseDeleteDialog();
                    }
                }}
                plan={selectedPlan}
                onSuccess={handleSuccessDelete}
            />

            <Snackbar
                open={snackbarOpen}
                autoHideDuration={3000}
                onClose={handleSnackbarClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert
                    onClose={handleSnackbarClose}
                    severity="success"
                    variant="filled"
                >
                    {snackbarMessage}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default AdminPlansPage;
